import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/ui/Logo';
import { FaYoutube, FaInstagram, FaTiktok } from 'react-icons/fa';

export default function LandingFooter() {
  const { t } = useTranslation();

  return (
    <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Logo className="h-12" forceDark />
            </div>
            <p className="text-gray-400 text-sm mb-5">
              {t('landing.footer.tagline')}
            </p>
            {/* Social Media Links */}
            <div className="flex items-center space-x-3">
              <a
                href="https://www.youtube.com/@PlanWiseSocial"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-gray-800 hover:bg-red-600 text-gray-400 hover:text-white transition-all duration-200"
                aria-label={t('board.platforms.youtube')}
              >
                <FaYoutube className="w-4 h-4" />
              </a>
              <a
                href="https://www.instagram.com/planwise.social/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-gray-800 hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 text-gray-400 hover:text-white transition-all duration-200"
                aria-label={t('board.platforms.instagram')}
              >
                <FaInstagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.tiktok.com/@planwise.social"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-gray-800 hover:bg-black text-gray-400 hover:text-white transition-all duration-200"
                aria-label={t('board.platforms.tiktok')}
              >
                <FaTiktok className="w-4 h-4" />
              </a>
              <a
                href="https://www.threads.com/@planwise.social"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg bg-gray-800 hover:bg-black text-gray-400 hover:text-white transition-all duration-200"
                aria-label="Threads"
              >
                {/* Threads official icon */}
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 192 192" xmlns="http://www.w3.org/2000/svg">
                  <path d="M141.537 88.988c-.827-.396-1.667-.777-2.518-1.143-1.482-27.307-16.403-42.94-41.457-43.1h-.139c-14.985 0-27.449 6.397-35.12 17.037l13.779 9.452c5.73-8.598 14.813-10.963 21.347-10.963h.076c8.58.057 15.164 2.496 19.553 7 3.125 3.247 5.241 7.741 6.328 13.395-7.475-1.336-16.581-1.724-26.337-1.104-24.973 1.466-41 14.046-39.947 32.876.538 9.535 5.214 17.705 13.208 22.982 6.745 4.47 15.491 6.656 24.603 6.175 12.033-.63 21.519-4.911 28.04-12.737 4.984-5.878 8.073-13.386 9.283-22.913 4.434 2.653 7.766 6.094 9.865 10.251 3.55 6.977 3.794 18.483-4.539 27.582-7.271 7.959-18.402 11.632-35.568 11.763-19.107-.148-33.077-6.124-41.533-17.748-7.818-10.825-11.818-26.606-11.959-46.701.141-20.095 4.141-35.876 11.959-46.701 8.456-11.624 22.426-17.6 41.533-17.748 19.24.15 33.411 6.146 42.06 17.816 4.254 5.749 7.395 13.211 9.389 22.188l16.302-3.987c-2.49-11.513-6.666-21.152-12.498-28.956C133.473 19.795 115.971 11.633 89.754 11.447h-.039C63.563 11.633 46.193 19.814 34.889 35.761 24.89 50.029 19.732 70.344 19.569 93.08v.017c.163 22.736 5.32 43.051 15.32 57.319 11.304 15.947 28.674 24.127 54.826 24.313h.039c23.242-.166 39.844-6.414 51.067-19.134 11.896-13.452 12.292-30.06 8.211-40.762-2.913-7.601-8.706-13.794-18.727-25.845l.232-.001zM98.478 127.13c-9.712.55-19.873-3.193-20.363-12.36-.36-6.718 4.765-14.24 19.174-15.133 1.72-.107 3.408-.156 5.07-.156 6.057 0 11.73.587 16.905 1.701-1.855 21.891-11.098 25.385-20.786 25.95z"/>
                </svg>
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{t('landing.footer.product')}</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><a href="/#features" className="hover:text-white transition-colors">{t('landing.footer.features')}</a></li>
              <li><Link to="/pricing" className="hover:text-white transition-colors">{t('landing.footer.pricing')}</Link></li>
              <li><Link to="/tools" className="hover:text-white transition-colors">{t('nav.public.freeTools')}</Link></li>
              <li><a href="/faq" className="hover:text-white transition-colors">FAQ</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{t('landing.footer.company')}</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/about" className="hover:text-white transition-colors">{t('landing.footer.about')}</Link></li>
              <li><Link to="/blogs" className="hover:text-white transition-colors">{t('landing.footer.blog')}</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">{t('landing.footer.contact')}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4">{t('landing.footer.legal')}</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/privacy" className="hover:text-white transition-colors">{t('landing.footer.privacy')}</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">{t('landing.footer.terms')}</Link></li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="border-t border-gray-800 pt-8 mb-6">
          <p className="text-center text-xs text-gray-500 uppercase tracking-widest mb-4 font-semibold">{t('landing.trust.heading')}</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {/* Google Verified */}
            <div className="flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span className="text-xs text-gray-300 font-medium">{t('landing.trust.google')}</span>
            </div>
            {/* Meta Approved */}
            <div className="flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="#E4405F" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
              </svg>
              <span className="text-xs text-gray-300 font-medium">{t('landing.trust.meta')}</span>
            </div>
            {/* TikTok Approved */}
            <div className="flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
              </svg>
              <span className="text-xs text-gray-300 font-medium">{t('landing.trust.tiktok')}</span>
            </div>
            {/* SSL */}
            <div className="flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-xs text-gray-300 font-medium">{t('landing.trust.ssl')}</span>
            </div>
            {/* GDPR */}
            <div className="flex items-center gap-2 bg-gray-800/60 border border-gray-700 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-xs text-gray-300 font-medium">{t('landing.trust.gdpr')}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-6 text-center text-sm text-gray-400">
          <p>{t('landing.footer.copyright', { year: new Date().getFullYear() })}</p>
        </div>
      </div>
    </footer>
  );
}
